import type { SelectOption } from 'noddi-ui-web';
import { getUserGroupContactSecondaryLabels } from 'noddi-util';
import { CustomerInfoCell } from './CustomerInfoCell';
import { customerInfoName, type CustomerInfoCellUserGroup } from './customerInfo';

/**
 * A customer option carrying what the dropdown row shows.
 *
 * `SelectOption` holds a label and nothing else, and the row is the whole point
 * of this picker: staff recognize a customer by phone number, not by a name that
 * repeats four times in the list.
 */
export type UserGroupSelectOption = SelectOption<number> & {
  /** Phone then email, personal customers only — `getUserGroupContactSecondaryLabels`. */
  secondaryLabels?: string[];
  isPersonal?: boolean;
  users?: CustomerInfoCellUserGroup['users'];
  type?: CustomerInfoCellUserGroup['type'];
};

/**
 * Structural rather than one nsdk record: the same shape reaches this from the
 * partner list record and from anything else carrying a name and its users.
 */
type UserGroupOptionInput = CustomerInfoCellUserGroup & { id: number };

export const buildUserGroupSelectOption = (userGroup: UserGroupOptionInput): UserGroupSelectOption => ({
  value: userGroup.id,
  // The name a person is called, not the group's own — `NoddiAsyncSelect` shows
  // the bare label once the option is picked, so the label has to stand alone.
  label: customerInfoName(userGroup),
  secondaryLabels: getUserGroupContactSecondaryLabels({
    isPersonal: userGroup.isPersonal ?? false,
    users: userGroup.users ?? []
  }),
  isPersonal: userGroup.isPersonal,
  users: userGroup.users,
  type: userGroup.type
});

/**
 * The same visual as `CustomerInfoCell`, minus its tooltip and link.
 *
 * Pass to `renderOption` on a customer select. The extras are optional so a
 * plain `SelectOption` still satisfies the parameter, which is what
 * `renderOption` is typed to hand it. A tooltip inside a cmdk row
 * fights the keyboard navigation it sits in, and nothing in a dropdown is a
 * link.
 */
export const renderUserGroupSelectOption = (
  option: SelectOption<string | number> & Pick<UserGroupSelectOption, 'label' | 'isPersonal' | 'type' | 'users'>
) => (
  <CustomerInfoCell
    userGroup={{
      name: option.label,
      isPersonal: option.isPersonal,
      type: option.type,
      users: option.users
    }}
    showTypeTooltip={false}
  />
);
